/**
 * Geometry for the strip's selected-table dot — the small pill that sits under
 * the open tables and slides between them as the player switches.
 *
 * It replaces the coloured ring that used to mark the selection. A ring could
 * not do the job once the turn ring became time-coded: both were contours drawn
 * on the same box, so "you are viewing this" and "this table wants your action"
 * competed for one channel. The dot lives OUTSIDE the tile entirely, which
 * leaves the tile free to say only what the table is doing — neutral frame when
 * idle, clock colour when acting.
 */

/** Pill dimensions and the gap below the tile, per the design mock. */
export const DOT_WIDTH = 16;
export const DOT_HEIGHT = 4;
export const DOT_GAP = 4;

export type DotPlacement = { left: number; top: number; width: number };

/** The subset of an element's box geometry the placement math needs. */
export type BoxMetrics = {
  offsetLeft: number;
  offsetTop: number;
  offsetWidth: number;
  offsetHeight: number;
};

/**
 * Where the dot sits for a given selected card.
 *
 * Centred on the card and DOT_GAP below the bottom of its tile — the framing
 * box, not the button. The two differ: the tile is absolutely positioned and
 * overhangs the button it sits in, so measuring the button alone would tuck the
 * dot up under the frame. `tile` is optional because a hand renders no tile in
 * the lobby strip, where the button's own box is the right reference.
 */
export const dotPlacement = (hand: BoxMetrics, tile?: BoxMetrics | null): DotPlacement => {
  const tileBottom = tile
    ? hand.offsetTop + tile.offsetTop + tile.offsetHeight
    : hand.offsetTop + hand.offsetHeight;
  // ONE fixed-width pill under the card being viewed. A version that stretched
  // to span every table on screen was tried and reverted (Jason 2026-08-17): a
  // wide bar reads as a progress track or a range, not as "you are here", and
  // the tiles already say which tables are on screen by simply being there.
  return {
    left: hand.offsetLeft + hand.offsetWidth / 2 - DOT_WIDTH / 2,
    top: tileBottom + DOT_GAP,
    width: DOT_WIDTH
  };
};

export const samePlacement = (a: DotPlacement | null, b: DotPlacement | null): boolean =>
  a === b || (!!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width);

/**
 * Last placement the dot settled on, remembered across mounts.
 *
 * Switching tables remounts the header the strip lives in, so a dot that
 * initialised from nothing would simply appear at its new home — no slide, which
 * is the one thing this indicator exists to do. Seeding the fresh mount with the
 * previous placement gives the transition something to travel from. Same reason
 * the strip remembers its fit and its cluster reach.
 */
let remembered: DotPlacement | null = null;

export const rememberDotPlacement = (placement: DotPlacement): void => {
  remembered = placement;
};

export const lastDotPlacement = (): DotPlacement | null => remembered;

/** Test seam — also called when the strip has no selection left to mark. */
export const forgetDotPlacement = (): void => {
  remembered = null;
};
